import { useState, useMemo } from "react";
import useGroups from "admin/hooks/useGroups";
import useRoles from "admin/hooks/useRoles";
import { sortFunctionalRoles } from "admin/utils/roles";
import { Download, Loader2, X, XCircle } from "lucide-react";
import * as Papa from "papaparse";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { apiMutate } from "~/utils/apiClient";

const { VITE_ACCESS_CONTROLLER_API } = import.meta.env;

const REQUIRED_COLUMNS = ["email", "first_name", "last_name"] as const;
const EXAMPLE_CSV =
  "email,first_name,last_name\njane.doe@example.com,Jane,Doe\njohn.smith@example.com,John,Smith";

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, "_");
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ParsedBulkUserRow {
  email: string;
  first_name: string;
  last_name: string;
}

interface CsvValidationResult {
  rows: ParsedBulkUserRow[];
  errors: string[];
}

interface BulkImportUsersOptions {
  users: { email: string; full_name: string }[];
  roles: string[];
  tags: string[];
  send_invite: boolean;
}

function parseAndValidateCsv(file: File): Promise<CsvValidationResult> {
  return file.text().then((text) => {
    const parseResult = Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: true,
    });

    const parseErrors = (parseResult.errors ?? []).filter(
      (e) => e.type === "FieldMismatch" || (e.type === "Quotes" && e.row !== undefined)
    );
    if (parseErrors.length > 0) {
      return {
        rows: [],
        errors: parseErrors.map((e) => `Row ${(e.row ?? 0) + 1}: ${e.message}`),
      };
    }

    const rawHeaders = parseResult.meta.fields ?? [];
    const normalizedToOriginal: Record<string, string> = {};
    rawHeaders.forEach((h) => {
      normalizedToOriginal[normalizeHeader(h)] = h;
    });

    const missing = REQUIRED_COLUMNS.filter((c) => !(c in normalizedToOriginal));
    if (missing.length > 0) {
      return {
        rows: [],
        errors: [
          `CSV must contain columns: email, first_name, last_name. Missing: ${missing.join(
            ", "
          )}. Found: ${rawHeaders.join(", ") || "(none)"}`,
        ],
      };
    }

    const data = parseResult.data;
    if (data.length === 0) {
      return { rows: [], errors: ["CSV has no data rows."] };
    }

    if (data.length > 500) {
      return {
        rows: [],
        errors: [`CSV contains ${data.length} rows. Maximum allowed is 500 users per import.`],
      };
    }

    const rows: ParsedBulkUserRow[] = [];
    const errors: string[] = [];
    const seenEmails = new Map<string, number>();

    data.forEach((row, index) => {
      const rowNum = index + 2; // 1-based, and +1 for header
      const email = row[normalizedToOriginal.email]?.trim() ?? "";
      const first_name = row[normalizedToOriginal.first_name]?.trim() ?? "";
      const last_name = row[normalizedToOriginal.last_name]?.trim() ?? "";

      if (!email) errors.push(`Row ${rowNum}: email is required`);
      else if (!EMAIL_REGEX.test(email))
        errors.push(`Row ${rowNum}: email must be a valid address`);
      if (!first_name) errors.push(`Row ${rowNum}: first_name is required`);
      if (!last_name) errors.push(`Row ${rowNum}: last_name is required`);

      if (!email || !EMAIL_REGEX.test(email) || !first_name || !last_name) {
        return;
      }

      const normalizedEmail = email.toLowerCase();
      const firstSeenRow = seenEmails.get(normalizedEmail);
      if (firstSeenRow !== undefined) {
        errors.push(
          `Row ${rowNum}: duplicate email "${email}" (first seen at row ${firstSeenRow})`
        );
        return;
      }
      seenEmails.set(normalizedEmail, rowNum);

      rows.push({ email, first_name, last_name });
    });

    return { rows, errors };
  });
}

interface BulkImportUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJobStarted: (jobId: string, totalUsers: number) => void;
}

type BulkImportTab = "roles" | "groups";

export function BulkImportUsersModal({ isOpen, onClose, onJobStarted }: BulkImportUsersModalProps) {
  const [activeTab, setActiveTab] = useState<BulkImportTab>("roles");
  const [file, setFile] = useState<File | null>(null);
  const [csvResult, setCsvResult] = useState<CsvValidationResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedGroupNames, setSelectedGroupNames] = useState<string[]>([]);
  const [sendInvite, setSendInvite] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showError } = useToast();
  const { data: roles = [] } = useRoles();
  const { data: allGroups = [] } = useGroups();

  const sortedRoles = useMemo(() => [...roles].sort(sortFunctionalRoles), [roles]);

  const validRowCount = csvResult?.rows.length ?? 0;
  const hasCsvErrors = (csvResult?.errors.length ?? 0) > 0;
  const canSubmit = validRowCount > 0 && !hasCsvErrors && !isSubmitting;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    setFile(chosen);
    setIsParsing(true);
    parseAndValidateCsv(chosen)
      .then((result) => {
        setCsvResult(result);
      })
      .catch(() => {
        setCsvResult({
          rows: [],
          errors: ["Failed to read or parse the file. Please check the format and try again."],
        });
      })
      .finally(() => {
        setIsParsing(false);
      });
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setFile(null);
    setCsvResult(null);
  };

  const handleDownloadExample = () => {
    const blob = new Blob([EXAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk_users_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleGroup = (groupName: string) => {
    setSelectedGroupNames((prev) =>
      prev.includes(groupName) ? prev.filter((n) => n !== groupName) : [...prev, groupName]
    );
  };

  const handleSubmit = async () => {
    if (!csvResult?.rows.length) {
      return;
    }
    setIsSubmitting(true);
    try {
      const apiPayload: BulkImportUsersOptions = {
        users: csvResult.rows.map((r) => ({
          email: r.email,
          full_name: `${r.first_name} ${r.last_name}`,
        })),
        roles: selectedRoleIds,
        tags: selectedGroupNames,
        send_invite: sendInvite,
      };
      const { job_id, total_users } = await apiMutate<{
        job_id: string;
        total_users: number;
      }>(`${VITE_ACCESS_CONTROLLER_API}/users/bulk/job`, "POST", apiPayload);
      onJobStarted(job_id, total_users);
      handleClose();
    } catch {
      showError("Failed to start bulk import. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setActiveTab("roles");
    setFile(null);
    setCsvResult(null);
    setSelectedRoleIds([]);
    setSelectedGroupNames([]);
    setSendInvite(true);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] bg-background flex flex-col p-0"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Bulk Import Users</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 space-y-6">
          <div className="space-y-2">
            <Label>CSV file</Label>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Upload a CSV with columns: <strong>email</strong>, <strong>first_name</strong>,{" "}
                <strong>last_name</strong> (all required).{" "}
              </p>
              <div className="flex items-stretch gap-2 w-full">
                <label className="flex flex-1 min-w-0 cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                  <div
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
                      "hover:border-primary/50 hover:bg-muted/30 transition-colors",
                      "min-h-10"
                    )}
                  >
                    {isParsing ? (
                      <>
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                        <span className="text-muted-foreground">Validating CSV…</span>
                      </>
                    ) : !file ? (
                      <>
                        <span className="flex-1 text-left text-muted-foreground">
                          Select file...
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="min-w-0 flex-1 truncate text-left">{file.name}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveFile();
                          }}
                          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </label>
              </div>
              <button
                type="button"
                onClick={handleDownloadExample}
                className="text-xs text-primary hover:text-primary/80 focus:outline-none focus:underline cursor-pointer flex items-center gap-1"
              >
                <Download className="h-4 w-4" /> Download Template
              </button>
              {csvResult && csvResult.errors.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {csvResult.errors.slice(0, 20).map((err, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                      <span className="text-xs text-muted-foreground">{err}</span>
                    </div>
                  ))}
                  {csvResult.errors.length > 20 && (
                    <div className="flex items-center gap-2">
                      <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                      <span className="text-xs text-muted-foreground">
                        … and {csvResult.errors.length - 20} more
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {validRowCount > 0 && !hasCsvErrors && (
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as BulkImportTab)}
              className="flex flex-col flex-1 min-h-0"
            >
              <Label className="mb-2">
                Configure {validRowCount} user{validRowCount !== 1 ? "s" : ""}
              </Label>
              <div className="flex justify-start">
                <TabsList className="w-fit">
                  <TabsTrigger value="roles">Functional Roles</TabsTrigger>
                  <TabsTrigger value="groups">Groups</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="roles" className="flex-1 min-h-0 pl-0">
                <div className="flex flex-col divide-y divide-border rounded-md border max-h-80 overflow-y-auto">
                  {sortedRoles.map((role) => (
                    <div key={role.id} className="flex gap-3 py-3 px-3 first:border-t-0">
                      <Checkbox
                        id={`bulk-role-${role.id}`}
                        checked={selectedRoleIds.includes(role.id)}
                        onCheckedChange={() => toggleRole(role.id)}
                      />
                      <div className="flex flex-col gap-1">
                        <Label
                          className="leading-[18px] cursor-pointer"
                          htmlFor={`bulk-role-${role.id}`}
                        >
                          {role.name}
                        </Label>
                        {role.description && (
                          <p className="text-xs text-muted-foreground">{role.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="groups" className="flex-1 min-h-0 pl-0">
                <div className="flex flex-col divide-y divide-border rounded-md border max-h-80 overflow-y-auto">
                  {allGroups.map((group) => (
                    <div key={group.name} className="flex gap-3 py-3 px-3 first:border-t-0">
                      <Checkbox
                        id={`bulk-group-${group.name}`}
                        checked={selectedGroupNames.includes(group.name)}
                        onCheckedChange={() => toggleGroup(group.name)}
                      />
                      <div className="flex flex-col gap-1">
                        <Label
                          className="leading-[18px] cursor-pointer"
                          htmlFor={`bulk-group-${group.name}`}
                        >
                          {group.display_name || group.name}
                        </Label>
                        {group.description && (
                          <p className="text-xs text-muted-foreground">{group.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>

        {validRowCount > 0 && !hasCsvErrors && (
          <div className="flex items-center gap-2 px-6">
            <Checkbox
              id="bulk-send-invite"
              checked={sendInvite}
              onCheckedChange={(v) => setSendInvite(v === true)}
            />
            <Label className="cursor-pointer text-sm font-normal" htmlFor="bulk-send-invite">
              Email instructions to sign in
            </Label>
          </div>
        )}

        <div className="flex justify-end gap-4 px-6 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={handleClose} size="lg">
            Cancel
          </Button>
          <Button
            type="button"
            size="lg"
            disabled={!canSubmit}
            className="min-w-[100px]"
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : validRowCount > 0 && !hasCsvErrors ? (
              `Create ${validRowCount} user${validRowCount !== 1 ? "s" : ""}`
            ) : (
              "Create users"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
